import { mdiArrowUp } from '@mdi/js';
import { Button } from '@mollybet/ui';
import { Currency } from 'common/classes/currency';
import {
  DangerButton,
  StyledButton,
  WarningButton,
} from 'common/components/buttons';
import { BaseTable, ColumnProp } from 'common/components/table/base';
import { DEFAULT_PAGE_SIZE } from 'common/components/table/config';
import {
  FilterProp,
  FilterTypes,
  useFiltration,
  FiltersContainer,
  OptionsFilterComponentHooks,
  GLEFilterComponentHooks,
} from 'common/components/table/filtering';
import { TableTitle } from 'common/components/table/title';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import {
  ClientTransferRequestsActions,
  ClientTransferRequest,
  ClientTransferRequestStatuses,
} from 'common/state/banking/transfers/actions';
import { clientTransferRequestsRecipe } from 'common/state/banking/transfers/recipes';
import {
  ClientTransferRequestsState,
  defaultClientTransferRequestsState,
} from 'common/state/banking/transfers/states';
import { doGetClientTransferRequests } from 'common/state/banking/transfers/thunkActions';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ModalMode } from '../agentBanking';

interface WithdrawalsTableProps {
  setSelectedRow: (transferRequest: ClientTransferRequest) => void;
  setModalMode: (mode: ModalMode) => void;
}

const STATUS_OPTIONS = [
  { value: undefined, label: 'All' },
  { value: ClientTransferRequestStatuses.OPEN, label: 'Open' },
  { value: ClientTransferRequestStatuses.COMPLETED, label: 'Completed' },
  { value: ClientTransferRequestStatuses.CANCELLED, label: 'Cancelled' },
];

const DateInputsContainer = styled.div`
  .eq {
    display: none;
  }
`;

export const WithdrawalsTable = ({
  setSelectedRow,
  setModalMode,
}: WithdrawalsTableProps) => {
  const [state, dispatch] = useProducerWithThunks<
    ClientTransferRequestsState,
    ClientTransferRequestsActions
  >(clientTransferRequestsRecipe, defaultClientTransferRequestsState);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const dropdownRef = useRef();

  const columns = useMemo(
    (): ColumnProp<ClientTransferRequest>[] => [
      {
        key: 'date',
        title: 'Date',
        valueFormatter: (row) => new Date(row.date).toLocaleDateString(),
      },
      {
        key: 'requester',
        title: 'Requester',
        valueFormatter: (row) => row.requester.split('/').slice(-2, -1)[0],
      },
      {
        key: 'receiver',
        title: 'Receiver',
        valueFormatter: (row) => row.receiver.split('/').slice(-2, -1)[0],
      },
      {
        key: 'sender',
        title: 'Sender',
        valueFormatter: (row) => row.sender.split('/').slice(-2, -1)[0],
      },
      { key: 'description', title: 'Description' },
      { key: 'reference', title: 'Reference' },
      { key: 'currency', title: 'Currency' },
      {
        key: 'amount',
        title: 'Amount',
        valueFormatter: (row) =>
          new Currency(row.amount, row.currency).toElement({ colour: false }),
        csvFormatter: (row) =>
          new Currency(row.amount, row.currency).toString({ symbol: false }),
      },
      { key: 'status', title: 'Status' },
      {
        key: 'action',
        title: 'Action',
        valueFormatter: (row) => (
          <div style={{ display: 'flex' }}>
            <StyledButton
              disabled={row.status !== ClientTransferRequestStatuses.OPEN}
              onClick={() => {
                setSelectedRow(row);
                setModalMode('withdrawalComplete');
              }}
            >
              Complete
            </StyledButton>
            <WarningButton
              disabled={row.status !== ClientTransferRequestStatuses.OPEN}
              onClick={() => {
                setSelectedRow(row);
                setModalMode('withdrawalTransfer');
              }}
            >
              Transfer
            </WarningButton>
            <DangerButton
              disabled={row.status !== ClientTransferRequestStatuses.OPEN}
              onClick={() => {
                setSelectedRow(row);
                setModalMode('withdrawalCancel');
              }}
            >
              Cancel
            </DangerButton>
          </div>
        ),
        isSortable: false,
      },
    ],
    [setSelectedRow, setModalMode]
  );

  const filters = useMemo(
    (): FilterProp<ClientTransferRequest>[] => [
      {
        key: 'status',
        label: 'Status',
        type: FilterTypes.OPTIONS,
        options: STATUS_OPTIONS,
        inputProps: {
          menuPortalTarget: dropdownRef.current,
        },
      },
      {
        key: 'date',
        label: 'Date',
        type: FilterTypes.DATE,
      },
    ],
    // eslint-disable-next-line
    [dropdownRef.current]
  );

  const [rows, filterComponents, filterHooks] = useFiltration(
    state.requests || [],
    filters
  );

  filterHooks.status =
    filterHooks?.status as OptionsFilterComponentHooks<ClientTransferRequest>;
  filterHooks.date =
    filterHooks?.date as GLEFilterComponentHooks<ClientTransferRequest>;
  filterHooks.date =
    filterHooks?.date as GLEFilterComponentHooks<ClientTransferRequest>;

  useEffect(() => {
    const filters = {
      limit: pageSize,
      offset: pageSize * (pageNo - 1),
    };

    filterHooks.status =
      filterHooks?.status as OptionsFilterComponentHooks<ClientTransferRequest>;
    filterHooks.date =
      filterHooks?.date as GLEFilterComponentHooks<ClientTransferRequest>;
    filterHooks.date =
      filterHooks?.date as GLEFilterComponentHooks<ClientTransferRequest>;

    if (filterHooks?.status?.value?.value)
      filters['status'] = filterHooks.status.value.value;
    if (filterHooks?.date?.value?.gt)
      filters['start_date'] = filterHooks.date.value.gt;
    if (filterHooks?.date?.value?.lt)
      filters['end_date'] = filterHooks.date.value.lt;

    dispatch(doGetClientTransferRequests(filters));
  }, [dispatch, filterHooks, pageSize, pageNo]);

  return (
    <>
      <TableTitle icon={mdiArrowUp} title="Withdrawal Requests" />
      <Button
        variant="primary"
        onClick={() => setModalMode('withdrawalNew')}
        style={{ marginLeft: '1rem' }}
      >
        Create New
      </Button>
      <FiltersContainer hooks={filterHooks}>
        {filterComponents.status}
        <br />
        <DateInputsContainer>{filterComponents.date}</DateInputsContainer>
      </FiltersContainer>
      <div ref={dropdownRef} />
      <br />
      <BaseTable
        columns={columns}
        rows={rows || []}
        csvName="Withdrawals"
        pagination={{
          count: state.count,
          pageNo,
          pageSize,
          setPageNo,
          setPageSize,
        }}
        isLoading={state.pending}
        error={state.error}
        isSortable={false}
      />
    </>
  );
};
